import { useEffect, useState } from "react";
import {
  assertNever,
  resaleRefund,
  type AttachmentEffect,
  type Composition,
  type ContaminatedTileState,
  type EdgeState,
  type FittedAttachment,
  type GridPos,
  type GridSnapshot,
  type NodeKind,
  type NodeState,
  type PacketState,
  type RecipeDef,
  type ResaleRule,
  type SpillCause,
} from "@reagent/sim";
import { canBuildNow, type InstalledUnit, pipesSoldBackByMove, useGameStore } from "../../app/gameStore";
import { playCue } from "../../audio/AudioEngine";
import { RecipePicker } from "./RecipePicker";
import {
  canRebind,
  costLabel,
  labourKw,
  nodeBindingLabel,
  recipesForKind,
  moveCredits,
  playDismantleRefund,
  playRule,
  shortfall,
  shortfallLabel,
  soldBackLabel,
} from "./liveBuild";
import { useInteractionStore } from "../../render/iso/interactionStore";
import { getCardView } from "../cards/cardViews";
import { ReagentChip, ReagentGlyph } from "../ReagentChip";
import { breakdown, reagentLook } from "../../reagents";
import { HAZARD_CONSEQUENCE, type HazardKind } from "../../render/hazardTells";
import "./tile-inspector.css";

/**
 * A fixed diegetic plate reading whatever `hoveredTile` points at — the mouse
 * and the keyboard cursor both write that one store field (`KeyboardControl`,
 * `GridPicker`), so this needs no input handling of its own to follow either.
 *
 * It lists EVERY component standing on the tile, because one tile can carry a
 * machine and the pipe runs passing through it at once, and for each one it
 * states every hazard counter that applies against its real limit — read
 * straight off the snapshot fields spec 001's task 1 added, never a constant
 * re-typed here — plus what happens at the limit, worded from
 * `HAZARD_CONSEQUENCE` so the panel says exactly what the on-board tells say.
 *
 * Unlike the on-board tells (`render/hazardTells.ts`, gated to "away from
 * resting" so the board doesn't clutter), this panel is opt-in — it only ever
 * describes the one tile under the cursor — so it states every applicable
 * counter unconditionally. A player reading it while planning, before
 * anything has gone wrong yet, is the point.
 *
 * Chemistry readouts — an input tank's feed (glyph, formula, common name,
 * rate, corrosive marking), a machine's buffer contents, and the balanced
 * equation for every recipe its kind can run — are spec 001 task 7's work,
 * read straight off `NodeState.feed` and `GridSnapshot.recipes`/
 * `corrosiveSpecies`, never hardcoded per stage.
 */
export function TileInspector() {
  const hovered = useInteractionStore((s) => s.hoveredTile);
  // The board clears `hoveredTile` the moment the pointer leaves it, which is
  // exactly when the mouse is travelling to this plate's buttons (spec 0067).
  // So the plate holds the last tile it read while the pointer is over it.
  const [last, setLast] = useState<GridPos | null>(null);
  const [over, setOver] = useState(false);
  useEffect(() => {
    if (hovered) setLast(hovered);
  }, [hovered]);
  const tile = hovered ?? (over ? last : null);
  const snapshot = useGameStore((s) => s.snapshot);
  const installedUnits = useGameStore((s) => s.installedUnits);
  const resaleRule = useGameStore((s) => s.resaleRule);

  const nodes = tile ? nodesAt(snapshot, tile) : [];
  const edges = tile ? edgesAt(snapshot, tile) : [];
  const contamination = tile ? contaminationAt(snapshot, tile) : null;

  return (
    <div className="tile-inspector" onPointerEnter={() => setOver(true)} onPointerLeave={() => setOver(false)}>
      <div className="tile-inspector__title">
        <span>Inspector</span>
        {tile && (
          <span className="tile-inspector__coord">
            {tile.x},{tile.y}
          </span>
        )}
      </div>

      <div className="tile-inspector__body">
        {!tile && <p className="tile-inspector__hint">Point at a tile to read what's fitted there.</p>}
        {tile && nodes.length === 0 && edges.length === 0 && !contamination && (
          <p className="tile-inspector__hint">Bare tile — nothing fitted here.</p>
        )}

        {contamination && <ContaminationReadout contamination={contamination} snapshot={snapshot} />}

        {nodes.map((node) => (
          <NodeReadout
            key={node.id}
            node={node}
            snapshot={snapshot}
            installedUnits={installedUnits}
            resaleRule={resaleRule}
          />
        ))}

        {edges.map((edge) => (
          <EdgeReadout
            key={edge.id}
            edge={edge}
            packets={packetsOn(snapshot, edge.id)}
            snapshot={snapshot}
            installedUnits={installedUnits}
            resaleRule={resaleRule}
          />
        ))}
      </div>
    </div>
  );
}

/** Exported for `tileInspector.test.ts`; pure, so it's tested directly against real engine snapshots. */
export function nodesAt(snapshot: GridSnapshot, tile: GridPos): readonly NodeState[] {
  return snapshot.nodes.filter((n) => !n.isDestroyed && n.pos.x === tile.x && n.pos.y === tile.y);
}

/** A pipe run can bend across several tiles; any waypoint counts as "on" it. */
export function edgesAt(snapshot: GridSnapshot, tile: GridPos): readonly EdgeState[] {
  return snapshot.edges.filter(
    (e) => !e.isBurst && e.path.some((p) => p.x === tile.x && p.y === tile.y),
  );
}

/** Find the contamination state at a tile position, if any. */
export function contaminationAt(snapshot: GridSnapshot, tile: GridPos): ContaminatedTileState | null {
  return (
    snapshot.contaminatedTiles.find((c) => c.pos.x === tile.x && c.pos.y === tile.y) ?? null
  );
}

/**
 * The refund a dismantle would pay right now for a unit occupying
 * `footprint` — pure, exported for `tileInspector.test.ts` (spec 0021 T4).
 * Age is always 0: nothing on the board outlives Setup in the current build,
 * the same assumption `engine/setup.ts`'s own dismantle handler makes.
 * Contaminated is "any tile in the footprint is contaminated right now"
 * (Open Question 1) — a pipe's whole path, not just the hovered waypoint.
 * `resaleRule` undefined (a standalone stage with no rule) falls through to
 * `resaleRefund`'s own fallback: the full price paid.
 */
export function refundPreview(
  costCredits: number,
  footprint: readonly GridPos[],
  snapshot: GridSnapshot,
  resaleRule: ResaleRule | undefined,
): number {
  const contaminated = footprint.some((pos) => contaminationAt(snapshot, pos) !== null);
  // In Play under a `liveBuild` rule a clean unit's refund is cut by
  // `playRefundPct` (spec 0067); the disposal fee is not.
  return playDismantleRefund(resaleRefund(costCredits, 0, contaminated, resaleRule), contaminated, playRule(snapshot));
}

/** Packets riding a pipe live in `packetsInFlight`, never in a node buffer. */
export function packetsOn(snapshot: GridSnapshot, edgeId: string): readonly PacketState[] {
  return snapshot.packetsInFlight.filter(
    (p) => p.location.kind === "onEdge" && p.location.edgeId === edgeId,
  );
}

/**
 * Spec 0031 T6: recipes this node can run — filtered by kind, and by
 * `node.recipeIds` when bound. An unbound node (recipeIds === null) sees all
 * kind-matching recipes; a bound node sees only those whose id is in recipeIds.
 * Exported for testing (spec 0031 criterion 9).
 */
export function recipesForNode(snapshot: GridSnapshot, node: NodeState): readonly RecipeDef[] {
  return snapshot.recipes.filter((r) => r.nodeKinds.includes(node.kind) && (node.recipeIds === null || node.recipeIds.includes(r.id)));
}

/**
 * The three I/O sink kinds with no process heat or stagnant buffer of their
 * own (spec 0032 criterion 9) — `NodeReadout` hides the heat and residence
 * hazard rows for exactly these, mirroring the engine's own hazard exemptions
 * (`hazards.ts`'s `accumulateResidence`/heatwave skip lists). Exported so the
 * exemption is checked against the real predicate rather than re-derived in
 * a test, the same reasoning `recipesForNode` was exported for (spec 0031).
 */
export function isHopperLikeSink(kind: NodeKind): boolean {
  return kind === "productHopper" || kind === "wasteBuyer" || kind === "vent";
}

export const NODE_KIND_LABEL: Record<NodeKind, string> = {
  inputTank: "Input tank",
  productHopper: "Product hopper",
  wasteBuyer: "Waste buyer",
  vent: "Vent",
  reactor: "Reactor",
  thermalRegulator: "Thermal regulator",
  separator: "Separator",
  phaseConverter: "Phase converter",
  catalyticBed: "Catalytic bed",
  incubator: "Incubator",
  flowMover: "Flow mover",
  flowBuffer: "Flow buffer",
};

export const MATERIAL_LABEL: Record<EdgeState["material"], string> = {
  glass: "Glass",
  ptfeLined: "PTFE-lined",
  hastelloy: "Hastelloy",
};

/**
 * A hazard row: which problem, its counter against its real limit, and what
 * happens there — never a card. `HAZARD_CONSEQUENCE` is the exact copy the
 * on-board tells use, so the two never disagree about the same hazard.
 */
function HazardRow({ hazard, value, limit }: { hazard: HazardKind; value: number; limit: number }) {
  return (
    <p className="tile-inspector__hazard">
      <strong>{HAZARD_ROW_LABEL[hazard]}:</strong> {value}/{limit}
      <span className="tile-inspector__consequence"> — {HAZARD_CONSEQUENCE[hazard]}</span>
    </p>
  );
}

/**
 * The dismantle-refund preview (spec 0021 T4): what clicking this tile right
 * now would pay, before the player commits — a click is an immediate sale in
 * Setup (`gameStore.ts`'s `dismantleAt`), so this panel is the only "before
 * you confirm" a player gets. Worded plainly enough to read without colour,
 * per the shapes-not-colours rule, even though it's carried in text alone.
 */
function RefundReadout({ refund }: { refund: number }) {
  return (
    <p
      className={
        refund >= 0
          ? "tile-inspector__line tile-inspector__refund"
          : "tile-inspector__line tile-inspector__refund tile-inspector__disposal-fee"
      }
    >
      {refund >= 0 ? `Sells back for ${refund} ₵` : `Disposal fee: ${-refund} ₵`}
    </p>
  );
}

/**
 * Move and Dismantle for one player-installed unit (spec 0067), each stating
 * its ₵ and kW BEFORE it is taken. The figures come from the snapshot's
 * `liveBuild` rule via `liveBuild.ts`; a refusal still surfaces as the store's
 * toast, so a button greyed here only ever saves a click that would be refused.
 */
function UnitActions({
  snapshot,
  refund,
  target,
}: {
  snapshot: GridSnapshot;
  refund: number;
  target: { kind: "node"; id: string; pos: GridPos } | { kind: "pipe"; id: string };
}) {
  const pendingMove = useGameStore((s) => s.pendingMove);
  const beginMoveNode = useGameStore((s) => s.beginMoveNode);
  const beginMovePipe = useGameStore((s) => s.beginMovePipe);
  const cancelMove = useGameStore((s) => s.cancelMove);
  const dismantleHardwareAt = useGameStore((s) => s.dismantleHardwareAt);
  const dismantlePipeAt = useGameStore((s) => s.dismantlePipeAt);

  if (!canBuildNow(snapshot)) return null;

  const moving =
    pendingMove !== null &&
    (target.kind === "node"
      ? pendingMove.kind === "node" && pendingMove.nodeId === target.id
      : pendingMove.kind === "pipe" && pendingMove.edgeId === target.id);
  const soldBack = target.kind === "node" ? pipesSoldBackByMove(snapshot, target.id).length : 0;

  const moveKw = labourKw(snapshot, "move");
  const moveShort = shortfall(snapshot, moveCredits(snapshot), moveKw);
  // kW only: a disposal fee is not checked against the budget by the button, so
  // the engine stays the one place that decides it.
  const dismantleKw = labourKw(snapshot, "dismantle");
  const dismantleShort = shortfall(snapshot, 0, dismantleKw);

  return (
    <div className="tile-inspector__actions">
      <button
        className="tile-inspector__action"
        disabled={!moving && moveShort !== null}
        onClick={() => {
          playCue("uiClick");
          if (moving) cancelMove();
          else if (target.kind === "node") beginMoveNode(target.id);
          else beginMovePipe(target.id);
        }}
      >
        {moving ? "Cancel move" : `Move — ${costLabel(moveCredits(snapshot), moveKw)}`}
      </button>
      {moveShort && !moving && <p className="tile-inspector__line">{shortfallLabel(moveShort)}</p>}
      {target.kind === "node" && (
        <p className="tile-inspector__line">
          {moving ? "Click the new tile. " : ""}
          {soldBackLabel(soldBack)}
        </p>
      )}

      <button
        className="tile-inspector__action"
        disabled={dismantleShort !== null}
        onClick={() => {
          playCue("uiClick");
          if (target.kind === "node") dismantleHardwareAt(target.id);
          else dismantlePipeAt(target.id);
        }}
      >
        {refund >= 0
          ? `Dismantle — refunds ${refund} ₵ · ${dismantleKw} kW`
          : `Dismantle — disposal fee ${-refund} ₵ · ${dismantleKw} kW`}
      </button>
      {dismantleShort && <p className="tile-inspector__line">{shortfallLabel(dismantleShort)}</p>}
    </div>
  );
}

/** Clean for one contaminated tile: a ₵ fee and no kW, so it works through an outage. */
function CleanAction({ snapshot, at }: { snapshot: GridSnapshot; at: GridPos }) {
  const cleanTileAt = useGameStore((s) => s.cleanTileAt);
  const rule = playRule(snapshot);
  if (!rule) return null;
  const short = shortfall(snapshot, rule.cleanFeeCredits, 0);
  return (
    <div className="tile-inspector__actions">
      <button
        className="tile-inspector__action"
        disabled={short !== null}
        onClick={() => {
          playCue("uiClick");
          cleanTileAt(at);
        }}
      >
        Clean — {costLabel(rule.cleanFeeCredits, 0)}
      </button>
      {short && <p className="tile-inspector__line">{shortfallLabel(short)}</p>}
    </div>
  );
}

/**
 * Rebind for any node `bindRecipe` accepts (spec 0068 OQ9), stage rig included,
 * so unlike Move/Dismantle it is not limited to player-bought units. The kW
 * labour is stated on the button before the picker opens; the picker lists only
 * recipes this node's kind runs, and has no unbound option (OQ7).
 */
function RebindAction({ snapshot, node }: { snapshot: GridSnapshot; node: NodeState }) {
  const [open, setOpen] = useState(false);
  const rebindRecipeAt = useGameStore((s) => s.rebindRecipeAt);
  if (!canBuildNow(snapshot) || !canRebind(snapshot, node)) return null;

  const kw = labourKw(snapshot, "rebind");
  const short = shortfall(snapshot, 0, kw);
  // Only a one-recipe binding can equal a one-recipe pick; the engine refuses
  // the rest as noEffect, so greying it here saves that click.
  const current = node.recipeIds?.length === 1 ? node.recipeIds[0]! : null;

  return (
    <div className="tile-inspector__actions">
      <button
        className="tile-inspector__action"
        disabled={short !== null}
        onClick={() => {
          playCue("uiClick");
          setOpen(!open);
        }}
      >
        {open ? "Cancel rebind" : `Rebind — ${costLabel(0, kw)}`}
      </button>
      {short && <p className="tile-inspector__line">{shortfallLabel(short)}</p>}
      {open && (
        <RecipePicker
          recipes={recipesForKind(snapshot, node.kind)}
          selectedId={current}
          disabledIds={new Set(current === null ? [] : [current])}
          onPick={(id) => {
            if (id === null) return;
            playCue("uiClick");
            rebindRecipeAt(node.id, [id]);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

export const HAZARD_ROW_LABEL: Record<HazardKind, string> = {
  heat: "Heat",
  pressure: "Pressure",
  wallHp: "Wall HP",
  catalystHealth: "Catalyst health",
  residence: "Residence",
  contamination: "Contamination",
  verminRisk: "Vermin risk",
};

/** Plain-language labels for spill causes. */
export const SPILL_CAUSE_LABEL: Record<SpillCause, string> = {
  burst: "Burst",
  leak: "Leak",
  explosion: "Explosion",
  move: "Loaded move",
  dismantle: "Loaded dismantle",
};

/**
 * What an attachment actually does, in the value it fits — never its card
 * name (the fitted-attachments list below is the one place that appears).
 */
export function attachmentEffectLabel(effect: AttachmentEffect): string {
  switch (effect.kind) {
    case "coolingJacket":
      return `sheds ${effect.heatShedPerTick} heat/tick`;
    case "ptfeLining":
      return "lines every standard pipe leaving this machine against corrosion";
    case "burstDisk":
      return `+${effect.extraPressureTolerance} pressure tolerance on lines it feeds`;
    case "guardBed":
      return `grants ${effect.charges} guard charges`;
    case "insulation":
      return `+${effect.extraCriticalTemp} critical temp`;
    case "surgeDrum":
      return `+${effect.extraBufferCapacity} buffer capacity`;
    case "lid":
      return "seals this waste buyer against vermin risk";
    default:
      return assertNever(effect, "TileInspector attachmentEffectLabel");
  }
}

function ContaminationReadout({
  contamination,
  snapshot,
}: {
  contamination: ContaminatedTileState;
  snapshot: GridSnapshot;
}) {
  const sourceNode = snapshot.nodes.find((n) => n.id === contamination.sourceId);
  const sourceEdge = snapshot.edges.find((e) => e.id === contamination.sourceId);

  const sourceName = sourceNode
    ? `${NODE_KIND_LABEL[sourceNode.kind]} at ${sourceNode.pos.x},${sourceNode.pos.y}`
    : sourceEdge
      ? `${MATERIAL_LABEL[sourceEdge.material]} pipe`
      : contamination.sourceId;

  return (
    <section className="tile-inspector__component">
      <header className="tile-inspector__component-title">Contamination</header>

      <p className="tile-inspector__line">
        <strong>Cause:</strong> {SPILL_CAUSE_LABEL[contamination.cause]}
      </p>

      <p className="tile-inspector__line">
        <strong>Source:</strong> {sourceName}
      </p>

      <p className="tile-inspector__line">
        <strong>Placement locked</strong> — this tile cannot accept new hardware or pipe routing until cleaned.
      </p>

      <p className="tile-inspector__hazard" style={{ marginTop: "4px" }}>
        {HAZARD_CONSEQUENCE.contamination}
      </p>

      <CleanAction snapshot={snapshot} at={contamination.pos} />
    </section>
  );
}

function NodeReadout({
  node,
  snapshot,
  installedUnits,
  resaleRule,
}: {
  node: NodeState;
  snapshot: GridSnapshot;
  installedUnits: Record<string, InstalledUnit>;
  resaleRule: ResaleRule | undefined;
}) {
  const nodeRecipes = recipesForNode(snapshot, node);
  // Only a player-bought machine has an entry — a stage-authored one has
  // none, matching the engine's own "notPlayerInstalled" dismantle refusal.
  const unit = canBuildNow(snapshot) ? installedUnits[node.id] : undefined;

  return (
    <section className="tile-inspector__component">
      <header className="tile-inspector__component-title">{NODE_KIND_LABEL[node.kind]}</header>

      {unit && (
        <>
          <RefundReadout refund={refundPreview(unit.costCredits, [node.pos], snapshot, resaleRule)} />
          <UnitActions
            snapshot={snapshot}
            refund={refundPreview(unit.costCredits, [node.pos], snapshot, resaleRule)}
            target={{ kind: "node", id: node.id, pos: node.pos }}
          />
        </>
      )}

      {canRebind(snapshot, node) && (
        <p className="tile-inspector__line">
          <strong>Runs:</strong> {nodeBindingLabel(snapshot, node)}
        </p>
      )}
      <RebindAction snapshot={snapshot} node={node} />

      {node.attachments.length > 0 && (
        <ul className="tile-inspector__attachments">
          {node.attachments.map((fitted, i) => (
            <AttachmentRow key={`${fitted.cardId}-${i}`} fitted={fitted} />
          ))}
        </ul>
      )}

      {node.guardCharges > 0 && (
        <p className="tile-inspector__line">guard charges left: {node.guardCharges}</p>
      )}

      {node.kind === "phaseConverter" && node.phaseThreshold !== null && (
        <p className="tile-inspector__line">converts at {node.phaseThreshold}°C</p>
      )}

      {node.kind === "reactor" && node.maxMultipleThisTurn !== null && (
        <p className="tile-inspector__line">Low Fire: fires at most {node.maxMultipleThisTurn} multiple{node.maxMultipleThisTurn === 1 ? "" : "s"} this turn</p>
      )}

      {node.kind === "incubator" && node.holdTicks > 0 && (
        <p className="tile-inspector__line">
          holds for {node.holdTicks} tick{node.holdTicks === 1 ? "" : "s"} · {Math.max(0, node.holdTicks - node.residenceTicks)} remaining
        </p>
      )}

      {node.kind === "inputTank" && node.sourcing !== null && (
        <p className="tile-inspector__line">
          sourcing: {node.sourcing.tier ?? "unchosen"}
          {node.supplyRemaining !== null && ` · ${node.supplyRemaining} units remaining`}
        </p>
      )}

      {node.feed && <FeedReadout feed={node.feed} corrosiveSpecies={snapshot.corrosiveSpecies} />}

      {node.buffer.length > 0 && (
        <ul className="tile-inspector__packets">
          {node.buffer.map((packet) => (
            <li key={packet.id} className="tile-inspector__packet">
              <ReagentChip composition={packet.composition} />
              <span
                className={
                  packet.isContaminated
                    ? "tile-inspector__packet-meta tile-inspector__contaminated"
                    : "tile-inspector__packet-meta"
                }
              >
                {packetMetaText(packet)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {nodeRecipes.length > 0 && (
        <ul className="tile-inspector__equations">
          {nodeRecipes.map((recipe) => (
            <EquationRow key={recipe.id} recipe={recipe} />
          ))}
        </ul>
      )}

      {!isHopperLikeSink(node.kind) && (
        <HazardRow hazard="heat" value={node.heatCounter} limit={node.criticalTemp} />
      )}
      {node.kind === "catalyticBed" && (
        <HazardRow hazard="catalystHealth" value={node.catalystHealth} limit={0} />
      )}
      {!isHopperLikeSink(node.kind) && (
        <HazardRow
          hazard="residence"
          value={node.residenceTicks}
          limit={snapshot.limits.sludgeResidenceTicks}
        />
      )}
    </section>
  );
}

/** One line of an input tank's feed readout — pure, so `tileInspector.test.ts` can check the
 * corrosive marking against real stage snapshots without rendering React. */
export interface FeedRow {
  readonly species: string;
  readonly label: string;
  readonly commonName: string;
  readonly units: number;
  readonly corrosive: boolean;
}

/**
 * A feed's rows: species, typeset formula, common name, rate, and whether the
 * snapshot's own `corrosiveSpecies` names it — never a hardcoded per-stage
 * list, since the same species (HCl, say) is corrosive on one stage's glass
 * lines and would be a false alarm read onto a stage that lined them.
 */
export function feedRows(feed: Composition, corrosiveSpecies: readonly string[]): readonly FeedRow[] {
  return breakdown(feed).map(({ look, units }) => ({
    species: look.species,
    label: look.label,
    commonName: look.commonName,
    units,
    corrosive: corrosiveSpecies.includes(look.species),
  }));
}

/**
 * Whether firing a recipe gives off heat, worded per spec 001 open question
 * 2: `RecipeDef` only knows `isExothermic`, so a recipe that isn't reads
 * literally as "doesn't heat the machine" rather than "endothermic", which
 * the data doesn't say.
 */
export function equationHeatText(recipe: RecipeDef): string {
  return recipe.isExothermic ? `gives off heat: ${recipe.heat} heat/firing` : "doesn't heat the machine";
}

/**
 * A packet row's readout: residence ticks, temperature, plus a Contaminated
 * marker spelled out in text — per the shapes-not-colours rule, contamination
 * is never conveyed by the accompanying colour alone (spec 0002 AC14).
 */
export function packetMetaText(packet: PacketState): string {
  return packet.isContaminated
    ? `residence ${packet.residenceTicks} · ${packet.temperature}°C · Contaminated`
    : `residence ${packet.residenceTicks} · ${packet.temperature}°C`;
}

function FeedReadout({ feed, corrosiveSpecies }: { feed: Composition; corrosiveSpecies: readonly string[] }) {
  return (
    <ul className="tile-inspector__feed">
      {feedRows(feed, corrosiveSpecies).map((row) => {
        const look = reagentLook(row.species);
        return (
          <li key={row.species} className="tile-inspector__feed-line">
            <ReagentGlyph shape={look.shape} color={look.color} />
            <span className="tile-inspector__feed-text">
              {row.label} — {row.commonName}, {row.units}/tick
              {row.corrosive && <strong className="tile-inspector__corrosive"> · Corrosive</strong>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A recipe as a balanced equation: inputs -> outputs, each side glyphed and
 * typeset the same way `ReagentChip` renders a packet, plus whether firing it
 * gives off heat.
 */
function EquationRow({ recipe }: { recipe: RecipeDef }) {
  return (
    <li className="tile-inspector__equation">
      <span className="tile-inspector__equation-formula">
        <ReagentChip composition={recipe.inputs} />
        <span className="tile-inspector__equation-arrow" aria-hidden>
          →
        </span>
        <ReagentChip composition={recipe.outputs} />
      </span>
      <span className="tile-inspector__equation-heat">{equationHeatText(recipe)}</span>
    </li>
  );
}

function AttachmentRow({ fitted }: { fitted: FittedAttachment }) {
  const card = getCardView(fitted.cardId);
  return (
    <li>
      <strong>{card?.name ?? fitted.cardId}</strong> — {attachmentEffectLabel(fitted.effect)}
    </li>
  );
}

function EdgeReadout({
  edge,
  packets,
  snapshot,
  installedUnits,
  resaleRule,
}: {
  edge: EdgeState;
  packets: readonly PacketState[];
  snapshot: GridSnapshot;
  installedUnits: Record<string, InstalledUnit>;
  resaleRule: ResaleRule | undefined;
}) {
  // Only a bought pipe has an entry — a stage-authored one has none.
  const unit = canBuildNow(snapshot) ? installedUnits[edge.id] : undefined;

  return (
    <section className="tile-inspector__component">
      <header className="tile-inspector__component-title">{MATERIAL_LABEL[edge.material]} pipe</header>

      {unit && (
        <>
          <RefundReadout refund={refundPreview(unit.costCredits, edge.path, snapshot, resaleRule)} />
          <UnitActions
            snapshot={snapshot}
            refund={refundPreview(unit.costCredits, edge.path, snapshot, resaleRule)}
            target={{ kind: "pipe", id: edge.id }}
          />
        </>
      )}

      {/* Spec 001 task 7: "what it is carrying". `ReagentChip` already
          typesets each species via `reagentLook(...).label` (e.g. "H₂SO₄"),
          so the composition below is the chemistry readout, not a stand-in
          for one. */}
      {packets.length > 0 && (
        <ul className="tile-inspector__packets">
          {packets.map((packet) => (
            <li key={packet.id}>
              <ReagentChip composition={packet.composition} />
            </li>
          ))}
        </ul>
      )}

      <HazardRow hazard="wallHp" value={edge.hp} limit={edge.maxHp} />
      <HazardRow hazard="pressure" value={edge.pressureCounter} limit={edge.burstLimit} />
    </section>
  );
}
